<script lang="ts" setup>
import { api } from '@/api';
import {
  AppPermissionsEnum,
  type CalibrationDataDto,
  CalibrationDataOverviewDto,
  CalibrationDataSource,
  CreateOtherQaEquipmentDto,
  CreateQaEquipmentRtSystemDto,
  instanceOfUnicheckDeviceDto,
  instanceOfUnicheckSimulatorDto,
  PtwRegistrationState,
  type QaEquipmentRtSystemDto,
} from '@/api/generator';
import Button from '@/components/buttons/Button.vue';
import DateTimeFormatter from '@/components/formatters/DateTimeFormatter.vue';
import FormSection from '@/components/forms/FormSection/FormSection.vue';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils.ts';
import InlineNotification from '@/components/notifications/InlineNotification.vue';
import OverviewTable from '@/components/OverviewTable/OverviewTable.vue';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import { usePermissionsStore } from '@/stores/permissionsStore.ts';
import type { I18nKey } from '@/utils/typings.ts';
import QaEquipmentActivateNewCalibrationDialog from '@/views/qaEquipment/components/QaEquipmentActivateNewCalibrationDialog.vue';
import DeactivatedChamberModal from '@/views/qaEquipment/components/DeactivatedChamberModal/DeactivatedChamberModal.vue';
import QaEquipmentSupportRequestCalibrationInvalidDialog from '@/views/qaEquipment/components/QaEquipmentSupportRequestCalibrationInvalidDialog.vue';
import QaEquipmentUploadCalibrationFileDialog from '@/views/qaEquipment/components/QaEquipmentUploadCalibrationFileDialog.vue';
import { useToggle } from '@vueuse/core';
import { useTranslation } from 'i18next-vue';
import { computed, ref, watchEffect } from 'vue';
import { isRegistrablePtwQaEquipment } from '@/views/qaEquipment/utils.ts';

type QaEquipmentCalibrationTabProps = {
  qaEquipment: Partial<
    QaEquipmentRtSystemDto | CreateQaEquipmentRtSystemDto | CreateOtherQaEquipmentDto
  >;
  calibrationData?: CalibrationDataOverviewDto;
};
const props = defineProps<QaEquipmentCalibrationTabProps>();

const isRegistered = computed(
  () =>
    isRegistrablePtwQaEquipment(props.qaEquipment) &&
    props.qaEquipment.registrationState === PtwRegistrationState.Registered,
);

// Permissions
const { hasPermissions } = usePermissionsStore();
const canActivate = computed(() =>
  hasPermissions([AppPermissionsEnum.LocalDosimetryAndQaEquipmentEdit]),
);

// Activate new calibration
const [isActivateCalibrationDialogOpen, toggleActivateCalibrationDialog] = useToggle(false);
// Contact Support
const [isContactSupportDialogOpen, toggleContactSupportDialog] = useToggle(false);

// Calibration table
const { t } = useTranslation();
const isUnicheck = computed(
  () =>
    instanceOfUnicheckDeviceDto(props.qaEquipment) ||
    instanceOfUnicheckSimulatorDto(props.qaEquipment),
);
const isCalibrationMissing = computed(
  () =>
    !!props.calibrationData &&
    !props.calibrationData.newDownloadedCalibration &&
    !props.calibrationData.existingCalibrations?.length,
);

// Upload calibration file
const [isUploadCalibrationFileDialogOpen, toggleUploadCalibrationFileDialog] = useToggle(false);

// The new downloaded calibration is shown as the first table row, with the
// "Activate now" button in place of the state chip.
type CalibrationRow = Partial<CalibrationDataDto> & { isNewCalibration?: boolean };
const calibrationRows = computed<CalibrationRow[]>(() => {
  const newCalibration = props.calibrationData?.newDownloadedCalibration;
  return [
    ...(newCalibration
      ? [
          {
            isNewCalibration: true,
            erpDeviceId: newCalibration.erpDeviceId ?? undefined,
            calibrationCertificateNumber: newCalibration.calibrationCertificateNumber ?? undefined,
            calibrationDate: newCalibration.calibrationDate ?? undefined,
            calibratedBy: newCalibration.calibratedBy,
            calibrationDataSource: CalibrationDataSource.PtwCloud,
            deactivatedChambersDC: newCalibration.deactivatedChambersDC,
            deactivatedChambersUC: newCalibration.deactivatedChambersUC,
          },
        ]
      : []),
    ...(props.calibrationData?.existingCalibrations ?? []),
  ];
});

// Type number of the device itself (e.g. "T10043"), used to resolve the
// detector layout. Not all DTO variants of the union carry it.
const deviceTypeNumber = computed(() =>
  'typeNumber' in props.qaEquipment ? (props.qaEquipment.typeNumber ?? undefined) : undefined,
);

// deactivatedChambersDC/UC arrive as comma-separated chamber id lists
function parseChamberIds(ids?: string | null): number[] {
  return (ids ?? '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean)
    .map(Number)
    .filter(Number.isFinite);
}

function hasDeactivatedChambers(item: CalibrationRow): boolean {
  return (
    parseChamberIds(item.deactivatedChambersDC).length > 0 ||
    parseChamberIds(item.deactivatedChambersUC).length > 0
  );
}

// Resolve creatorUserId to a display name for the "Activated by" column
const userNamesById = ref(new Map<number, string>());
watchEffect(async () => {
  if (!props.calibrationData?.existingCalibrations?.length || userNamesById.value.size) {
    return;
  }
  const { result } = await api.usersApi.getUsersCatalog();
  if (result) {
    userNamesById.value = new Map(result.map((user) => [user.id, user.fullName]));
  }
});
const activatedByName = (creatorUserId?: number | null) =>
  (creatorUserId != null && userNamesById.value.get(creatorUserId)) || EMPTY_FIELD_SIGN;

const calibrationTableHeaders = computed(() => [
  {
    title: `${t('CalibrationCertificateNo')}`,
    key: 'calibrationCertificateNumber',
    sortable: false,
  },
  {
    title: `${t('CalibrationDate')}`,
    key: 'calibrationDate',
    sortable: false,
  },
  {
    title: `${t('CalibrationLaboratory')}`,
    key: 'calibratedBy',
    sortable: false,
  },
  {
    title: `${t('Source')}`,
    key: 'source',
    sortable: false,
  },
  ...(isUnicheck.value
    ? []
    : [
        {
          title: `${t('DeactivatedChambers')}`,
          key: 'deactivatedChambers',
          sortable: false,
        },
      ]),
  {
    title: `${t('ActivationDate')}`,
    key: 'activationDate',
    sortable: false,
  },
  {
    title: `${t('ActivatedBy')}`,
    key: 'activatedBy',
    sortable: false,
  },
  {
    title: '',
    key: 'calibrationState',
    sortable: false,
  },
]);

const calibrationStateColor = (state: CalibrationDataDto['calibrationState']) => {
  switch (state) {
    case 'Active':
      return 'success';
    case 'Outdated':
      return 'warning';
    case 'Invalid':
      return 'error';
    default:
      return 'primary';
  }
};
</script>

<template>
  <template v-if="!isRegistered">
    <p class="text-body-1 text-primary-lighten-2 mt-4">
      {{ $t('CalibrationInformationOnlyWhenDeviceRegisteredMsg') }}
    </p>
  </template>
  <template v-else>
    <!-- New Calibration is available -->
    <template
      v-if="isRegistrablePtwQaEquipment(qaEquipment) && calibrationData?.newDownloadedCalibration"
    >
      <QaEquipmentActivateNewCalibrationDialog
        :id="qaEquipment.id"
        v-model="isActivateCalibrationDialogOpen"
        :newCalibrationData="calibrationData.newDownloadedCalibration"
        :currentCalibrationData="calibrationData.existingCalibrations?.[0]"
        @click:contactSupport="
          toggleActivateCalibrationDialog(false);
          toggleContactSupportDialog(true);
        "
      />
    </template>
    <QaEquipmentSupportRequestCalibrationInvalidDialog
      v-if="calibrationData?.existingCalibrations?.length"
      v-model="isContactSupportDialogOpen"
      :newCalibrationData="calibrationData.newDownloadedCalibration"
      :currentCalibrationData="calibrationData.existingCalibrations[0]"
      :lastReportedByDevice="calibrationData.lastReportedByDevice"
    />

    <!-- List of calibrations -->
    <template v-if="calibrationRows.length">
      <v-row>
        <v-col cols="12">
          <h4 class="mb-4">{{ $t('CalibrationInformation') }}</h4>

          <InlineNotification
            v-if="calibrationData?.newDownloadedCalibration"
            class="w-100 mb-4"
            data-testid="InlineNotification_NewCalibrationAvailable"
            :value="{
              type: 'warning',
              message: $t('ANewCalibrationCertificateIsAvailableMsg'),
            }"
          />

          <OverviewTable
            :showItemsCount="false"
            :zebra="true"
            :noSpacing="true"
            :headers="calibrationTableHeaders"
            :items="calibrationRows"
            data-testid="DataTable_CalibrationInformation"
          >
            <template #item.calibrationDate="{ item }">
              <DateTimeFormatter :value="item.calibrationDate" format="shortDateOnly" />
            </template>

            <template #item.calibratedBy="{ item }">
              {{ item.calibratedBy || EMPTY_FIELD_SIGN }}
            </template>

            <template #item.source="{ item }">
              <div
                v-if="item.calibrationDataSource || item.fileName"
                class="d-flex flex-column align-start py-1"
              >
                <v-chip
                  v-if="item.calibrationDataSource"
                  label
                  size="small"
                  :text="$t(`CalibrationDataSourceEnum.${item.calibrationDataSource}` as I18nKey)"
                  class="mb-2"
                />
                <span v-if="item.fileName">{{ item.fileName }}</span>
              </div>
              <template v-else>
                {{ EMPTY_FIELD_SIGN }}
              </template>
            </template>

            <template #item.activationDate="{ item }">
              <DateTimeFormatter :value="item.activationDate ?? undefined" format="shortDateOnly" />
            </template>

            <template #item.activatedBy="{ item }">
              {{ activatedByName(item.creatorUserId) }}
            </template>

            <template #item.deactivatedChambers="{ item }">
              <div v-if="hasDeactivatedChambers(item)" class="d-flex align-center ga-2 py-1">
                <div class="d-flex flex-column align-start ga-1">
                  <v-chip
                    v-if="item.deactivatedChambersDC"
                    label
                    size="small"
                    :text="`DC: ${item.deactivatedChambersDC}`"
                  />
                  <v-chip
                    v-if="item.deactivatedChambersUC"
                    label
                    size="small"
                    :text="`UC: ${item.deactivatedChambersUC}`"
                  />
                </div>

                <DeactivatedChamberModal
                  class="ml-auto"
                  :typeNumber="deviceTypeNumber"
                  :model="qaEquipment.model"
                  :deactivatedChambersDc="parseChamberIds(item.deactivatedChambersDC)"
                  :deactivatedChambersUc="parseChamberIds(item.deactivatedChambersUC)"
                />
              </div>
              <template v-else>
                {{ EMPTY_FIELD_SIGN }}
              </template>
            </template>

            <template #item.calibrationState="{ item }">
              <Tooltip
                v-if="item.isNewCalibration"
                :showPointer="true"
                :disabled="canActivate"
                forceActive
              >
                <template #target="{ props: tooltipTargetProps }">
                  <div v-bind="tooltipTargetProps" class="d-inline-block">
                    <PrimaryVBtn
                      :disabled="
                        !canActivate ||
                        calibrationData?.existingCalibrations?.[0]?.calibrationState === 'Invalid'
                      "
                      :text="$t('ActivateNow')"
                      @click="toggleActivateCalibrationDialog(true)"
                    />
                  </div>
                </template>
                <template #tooltip>
                  {{ $t('NoPermissionsToActivateCalibrationCertificateMsg') }}
                </template>
              </Tooltip>
              <v-chip
                v-else-if="item.calibrationState"
                label
                size="small"
                :color="calibrationStateColor(item.calibrationState)"
                :text="item.calibrationState"
              />
              <template v-else>
                {{ EMPTY_FIELD_SIGN }}
              </template>
            </template>
          </OverviewTable>

          <Button
            v-if="calibrationData?.existingCalibrations?.[0]?.calibrationState === 'Invalid'"
            variant="secondary"
            class="mt-4"
            :label="$t('ContactPtwCustomerSupport')"
            @click="toggleContactSupportDialog(true)"
          />
        </v-col>
      </v-row>
    </template>

    <!-- Calibration missing -->

    <template v-if="isCalibrationMissing">
      <v-row>
        <v-col cols="12">
          <h4 class="mb-4">{{ $t('CalibrationInformation') }}</h4>
          <InlineNotification
            class="w-100"
            data-testid="InlineNotification_CalibrationMissing"
            :value="{
              type: 'warning',
              title: $t('CalibrationMissingTitle'),
              message: $t('UploadAndActivateLatestCalibrationFileMsg'),
            }"
          />
        </v-col>
      </v-row>
    </template>

    <!-- Upload calibration file -->
    <template v-if="calibrationData?.isCalibrationUploadAllowed">
      <v-row>
        <v-col cols="12">
          <FormSection color="primary-lighten-5" class="pa-6" :title="$t('UploadCalibrationFile')">
            <p class="text-body-1 mb-4">
              {{ $t('UploadCalibrationFileAssociatedWithLatestCalibrationMsg') }}
            </p>
            <PrimaryVBtn
              :text="$t('UploadCalibrationFile')"
              @click="toggleUploadCalibrationFileDialog(true)"
            />
          </FormSection>
        </v-col>
      </v-row>
      <QaEquipmentUploadCalibrationFileDialog
        v-model="isUploadCalibrationFileDialogOpen"
        :qaEquipment="qaEquipment"
      />
    </template>
  </template>
</template>
